import { enumPresentation } from "@/lib/enum";
import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiErrorAlert } from "@/components/api-error-alert";
import { SettingsSection } from "@/components/page-templates";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Text } from "@/components/ui/Text";
import type { OktaIdentityProviderConnection } from "@gram/client/models/components/oktaidentityproviderconnection.js";
import { useVerifyIdentityProviderConnectionMutation } from "@gram/client/react-query/verifyIdentityProviderConnection.js";

import { STEP_AFFORDANCES } from "./checklistAffordances";
import { ConnectionChecklist } from "./ConnectionChecklist";
import { ConnectionSetupProgress } from "./ConnectionSetupProgress";
import {
  ConnectionFacts,
  ConnectionScopes,
  ReplaceClientSecretButton,
} from "./OktaConnectionDetails";
import {
  CreateConnectionForm,
  SetupMethodChooser,
} from "./OktaConnectionForms";
import { OktaSignInSection } from "./OktaSignInSection";
import { RevokeConnectionButton } from "./RevokeConnectionButton";
import {
  CONNECTION_STATUS,
  connectionStep,
  isConnected,
  LAST_ERROR_LABELS,
  VERIFICATION_REASON_LABELS,
  usesClientSecret,
  type ConnectionStep,
  type LiveConnection,
} from "../../connectionView";
import {
  inlineError,
  invalidateIdentityProviderQueries,
  SESSION_SECURITY,
} from "../../identityProviderQueries";
import {
  CHECKLIST_SECTION_ID,
  CONNECTION_SECTION_ID,
  scrollToConnectionCard,
} from "../../tabs";

function VerificationOutcome({
  connection,
}: {
  connection: LiveConnection;
}): JSX.Element | null {
  const reasons = connection.verificationReasons;
  if (!connection.lastError && reasons.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      {connection.lastError && (
        <Alert variant="error">
          <Text variant="small">{LAST_ERROR_LABELS[connection.lastError]}</Text>
        </Alert>
      )}
      {reasons.length > 0 && (
        <Alert variant="warning">
          <div className="flex flex-col gap-1">
            <Text variant="small" className="font-medium">
              The connection needs attention
            </Text>
            <ul className="list-disc space-y-0.5 pl-4">
              {reasons.map((reason) => (
                <li key={reason}>
                  <Text variant="small">
                    {VERIFICATION_REASON_LABELS[reason]}
                  </Text>
                </li>
              ))}
            </ul>
            {connection.missingScopes.length > 0 && (
              <Text variant="small">
                Grant {connection.missingScopes.join(", ")} to the app in Okta,
                then re-verify. Application sync stays paused until verification
                passes.
              </Text>
            )}
          </div>
        </Alert>
      )}
    </div>
  );
}

const FOOTER_HINTS: Record<ConnectionStep, string> = {
  unknown: "Refresh the page to check the connection status.",
  connected: "Re-verify after changing the app’s permissions in Okta.",
  repair: "Re-verify after changing the app’s permissions in Okta.",
  verify: "Verify once the app is set up to use the public key URL (JWKS).",
  submit_client_id: "Verification runs after the client ID is submitted.",
};

const CLIENT_SECRET_VERIFY_HINT =
  "Verify once the app’s client secret is submitted and its permissions are granted.";

function footerHint(connection: LiveConnection, step: ConnectionStep): string {
  if (step === "verify" && usesClientSecret(connection)) {
    return CLIENT_SECRET_VERIFY_HINT;
  }
  return FOOTER_HINTS[step];
}

function connectionDescription(connection: LiveConnection): string {
  if (usesClientSecret(connection)) {
    return "Speakeasy uses this API Services app, installed from the Okta Integration Network, to connect to Okta. It authenticates with the app’s client ID and client secret; the secret is stored encrypted and can be replaced below.";
  }
  return "Speakeasy uses this API Services app to connect to Okta. Okta reads public keys from the public key URL (JWKS) below to check that requests come from Speakeasy. Private keys stay with Speakeasy.";
}

const SECTION_LINK = "text-sm underline underline-offset-4";

function NextStepCallout({
  title,
  body,
  children,
}: {
  title: string;
  body: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3 border p-4">
      <Text className="font-medium">{title}</Text>
      <Text muted small>
        {body}
      </Text>
      {children}
    </div>
  );
}

function ConnectionCard({
  connection,
}: {
  connection: LiveConnection;
}): JSX.Element {
  const location = useLocation();
  useEffect(() => {
    if (location.hash === `#${CONNECTION_SECTION_ID}`) scrollToConnectionCard();
  }, [location.hash, location.key]);
  const queryClient = useQueryClient();
  const step = connectionStep(connection);
  const status = enumPresentation(CONNECTION_STATUS, connection.status, {
    variant: "neutral",
    label: "Unknown",
  });
  const verify = useVerifyIdentityProviderConnectionMutation({
    onSuccess: (updated) => {
      if (updated.status === "verified") {
        toast.success("Connection verified");
      } else {
        toast.warning(
          "Verification needs attention. Review the results below.",
        );
      }
      void invalidateIdentityProviderQueries(queryClient).then(
        scrollToConnectionCard,
      );
    },
    onError: inlineError,
  });
  const runVerify = () => {
    if (verify.isPending) return;
    verify.mutate({
      security: SESSION_SECURITY,
      request: {
        verifyIdentityProviderConnectionRequestBody: { id: connection.id },
      },
    });
  };

  return (
    <SettingsSection id={CONNECTION_SECTION_ID}>
      <SettingsSection.Header>
        <div className="flex flex-wrap items-center gap-3">
          <SettingsSection.Title>Connection</SettingsSection.Title>
          <Badge variant="neutral" size="sm">
            Okta
          </Badge>
          <Badge variant={status.variant} size="sm">
            {status.label}
          </Badge>
        </div>
        <SettingsSection.Description>
          {connectionDescription(connection)}
        </SettingsSection.Description>
      </SettingsSection.Header>
      <SettingsSection.Panel>
        <SettingsSection.Body>
          {step === "submit_client_id" && (
            <NextStepCallout
              title="Next: set up your Okta app"
              body="Follow the Connect steps above. The last step takes the app's client ID and verifies access."
            >
              <a className={SECTION_LINK} href={`#${CHECKLIST_SECTION_ID}`}>
                Go to Connect steps
              </a>
            </NextStepCallout>
          )}
          {(step === "verify" || step === "repair") && (
            <NextStepCallout
              title={
                step === "repair"
                  ? "Next: fix the connection issues"
                  : "Next: verify your connection"
              }
              body={`Check the app's permissions (scopes), admin role, and ${usesClientSecret(connection) ? "client secret" : "public key"} settings in Okta, then verify access.${
                step === "repair"
                  ? " Application sync is paused until verification passes."
                  : ""
              }`}
            >
              <a className={SECTION_LINK} href={`#${CHECKLIST_SECTION_ID}`}>
                Review setup
              </a>
              <Button disabled={verify.isPending} onClick={runVerify}>
                {verify.isPending ? "Verifying..." : "Verify connection"}
              </Button>
            </NextStepCallout>
          )}
          <ConnectionFacts connection={connection} />
          <VerificationOutcome connection={connection} />
          <ApiErrorAlert error={verify.error} />
        </SettingsSection.Body>
        <SettingsSection.Body className="border-t">
          <ConnectionScopes connection={connection} />
        </SettingsSection.Body>
        <SettingsSection.Footer>
          <SettingsSection.FooterHint>
            {footerHint(connection, step)}
          </SettingsSection.FooterHint>
          <SettingsSection.FooterActions>
            <RevokeConnectionButton connection={connection} />
            {usesClientSecret(connection) && connection.clientIdSubmitted && (
              <ReplaceClientSecretButton connection={connection} />
            )}
            {step === "connected" && (
              <Button
                variant="secondary"
                disabled={verify.isPending}
                onClick={runVerify}
              >
                {verify.isPending ? "Verifying..." : "Re-verify"}
              </Button>
            )}
          </SettingsSection.FooterActions>
        </SettingsSection.Footer>
      </SettingsSection.Panel>
    </SettingsSection>
  );
}

function ChecklistSection({
  connection,
}: {
  connection: LiveConnection;
}): JSX.Element {
  return (
    <SettingsSection id={CHECKLIST_SECTION_ID}>
      <SettingsSection.Panel>
        <SettingsSection.Body>
          <ConnectionChecklist
            connection={connection}
            affordances={STEP_AFFORDANCES}
            groups={["connect"]}
          />
        </SettingsSection.Body>
      </SettingsSection.Panel>
    </SettingsSection>
  );
}

export function OktaConnectionTab({
  connection,
}: {
  connection: OktaIdentityProviderConnection | undefined;
}): JSX.Element {
  if (!isConnected(connection)) {
    return <CreateConnectionForm />;
  }

  return (
    <div className="flex flex-col gap-10">
      <ConnectionSetupProgress connection={connection} />
      {connection.status === "pending" && !connection.clientIdSubmitted && (
        <SetupMethodChooser connection={connection} />
      )}
      <ChecklistSection connection={connection} />
      <OktaSignInSection connection={connection} />
      <ConnectionCard connection={connection} />
    </div>
  );
}
